import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { FaPlus, FaTrash, FaSort, FaSortUp, FaSortDown, FaPrint } from "react-icons/fa";
import PageTitle from "../pages/PageTitle";
import { useNavigate } from "react-router-dom";
import { deleteGateEntryAPIURL, getGateEntryAPIURL, printGateEntryAPIURL } from "../commonServices/TankerEntryAPIURL";
import { toast } from "react-toastify";
import DeleteConfirmModal from "../pages/DeleteConfirmModal";
import moment from "moment";
import { pdfPathAPIURL } from "../commonServices/APIURL";
import { useSelector } from "react-redux";
import Loader from "../pages/Loader";

const TankerEntryTable = () => {
    const authUser = useSelector((state) => state.auth.user);
    // console.log(authUser?.token)
    const navigate = useNavigate();

    const [loader, setLoader] = useState(false);

    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [selectedPrintId, setSelectedPrintId] = useState(null);
    const [copies, setCopies] = useState(1);


    // DELETE modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const toggleDeleteModal = () => setDeleteModalOpen(!deleteModalOpen);

    // TABLE DATA
    const [data, setData] = useState([]);

    // SEARCH
    const [searchTerm, setSearchTerm] = useState("");

    // SORTING
    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");

    // ================================
    // PAGINATION STATES
    // ================================
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);

    const handleRowsChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1); // reset
    };

    const handleNext = () => {
        setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
    };

    const handlePrev = () => {
        setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
    };

    // FETCH DATA
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        if (!authUser?.token || authUser.token.trim() === "") {
            toast.error("Authorization token missing!");
            return; // 🔥 Stop here — do not call API
        }
        setLoader(true);
        try {
            // console.log(authUser.token)
            const resp = await axios.get(getGateEntryAPIURL, {
                headers: {
                    'Authorization': `Bearer ${authUser.token}`,   // 👈 Add token here
                    // OR if your API expects another header name:
                    // "x-access-token": authUser.token
                }
            });

            if (Array.isArray(resp.data)) {
                setData(resp.data);
                setLoader(false);
            }
        } catch (error) {
            console.error("Error fetching gate entries:", error);
            setLoader(false);
            toast.error("Something went wrong, please check!")
        }
    };

    // ================================
    // SEARCH FILTER (WORKING NOW)
    // ================================
    const filteredData = data.filter((item) => {
        const q = searchTerm.toLowerCase();
        return (
            item.truckNo?.toLowerCase().includes(q) ||
            item.routeNo?.toString().includes(q) ||
            item.societyId?.toString().includes(q) ||
            item.uniqueId?.toString().includes(q)
        );
    });

    // ================================
    // SORT FUNCTION
    // ================================
    const handleSort = (field) => {
        const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
        setSortField(field);
        setSortOrder(order);
    };

    const sortedFilteredData = [...filteredData].sort((a, b) => {
        if (!sortField) return 0;
        if (a[sortField] < b[sortField]) return sortOrder === "asc" ? -1 : 1;
        if (a[sortField] > b[sortField]) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    // ================================
    // PAGINATION (NOW WORKS WITH SEARCH)
    // ================================
    const totalRows = sortedFilteredData.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalRows);

    const paginatedData = sortedFilteredData.slice(startIndex, endIndex);

    // SORT ICON
    const sortIcon = (field) => {
        if (sortField !== field)
            return <FaSort className="inline ml-1 text-gray-400" />;

        return sortOrder === "asc"
            ? <FaSortUp className="inline ml-1 text-indigo-600" />
            : <FaSortDown className="inline ml-1 text-indigo-600" />;
    };

    // DELETE ENTRY
    const handleDelete = async (id) => {
        if (!authUser?.token || authUser.token.trim() === "") {
            toast.error("Authorization token missing!");
            return; // 🔥 Stop here — do not call API
        }
        setLoader(true);
        try {
            const resp = await axios.delete(`${deleteGateEntryAPIURL}?id=${id}`, {
                headers: {
                    'Authorization': `Bearer ${authUser.token}`,   // 👈 Add token here
                    // OR if your API expects another header name:
                    // "x-access-token": authUser.token
                }
            });

            if (resp.data === "DELETED") {
                setLoader(false);
                toast.success("Entry deleted successfully!");
                fetchData();
            } else {
                setLoader(false);
                toast.error("Delete failed!");
            }
        } catch (error) {
            console.error(error);
            setLoader(false);
            toast.error("Network error while deleting!");
        }
    };

    const handlePrint = (id) => {
        setSelectedPrintId(id);
        setCopies(1); // default value
        setPrintModalOpen(true);
    };


    const confirmPrint = async () => {
        if (!authUser?.token) {
            toast.error("Authorization token missing!");
            return;
        }

        if (!copies || Number(copies) <= 0) {
            toast.error("Enter valid copies!");
            return;
        }

        setLoader(true);

        try {
            const resp = await axios.get(
                `${printGateEntryAPIURL}?id=${selectedPrintId}&copies=${copies}`,
                {
                    headers: {
                        Authorization: `Bearer ${authUser.token}`,
                    },
                }
            );

            if (resp.data === "NOTFOUND") {
                toast.warn("Not found!");
            } else {
                window.open(`${pdfPathAPIURL}${resp.data}`);
            }
        } catch (err) {
            toast.error("Network error while printing!");
            console.error(err);
        }

        setLoader(false);
        setPrintModalOpen(false);
    };



    const handleDeleteConfirmed = () => {
        handleDelete(selectedId);
        setDeleteModalOpen(false);
    };

    // ----------------------------------------
    // RETURN UI
    // ----------------------------------------
    return (
        <>
            {
                loader == true
                    ?
                    <Loader />
                    :
                    <>
                        <PageTitle title="Gate Entry Table" />

                        {/* TABLE SECTION */}
                        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200">

                            {/* TOP BAR */}
                            <div className="flex flex-col md:flex-row mb-6 items-start md:items-center">

                                {/* Left Button (only shows if role != 2) */}
                                {authUser?.roleId != 2 && (
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 
                text-white px-4 py-2 rounded-lg shadow transition"
                                        onClick={() => navigate("/app/gate-entryF/1/0")}
                                    >
                                        <FaPlus /> New Entry
                                    </button>
                                )}

                                {/* █████ SPACE PUSHER █████ */}
                                <div className="flex-grow"></div>

                                {/* Search Box */}
                                <div className="relative w-full md:w-80 mt-3 md:mt-0">
                                    <input
                                        type="text"
                                        placeholder="Search here..."
                                        className="border border-gray-300 w-full px-4 py-2 rounded-lg shadow-sm 
                        focus:ring-2 focus:ring-indigo-500"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                    <span className="absolute right-4 top-2.5 text-gray-400">🔍</span>
                                </div>
                            </div>


                            {/* TABLE */}
                            <div className="w-full overflow-x-auto">
                                <Table bordered responsive className="min-w-full border border-gray-300 rounded-md">
                                    <thead>
                                        <tr className="bg-indigo-50 text-indigo-700 border-b border-gray-300 text-center uppercase text-sm">

                                            <th className="px-2 py-1 border border-indigo-300 cursor-pointer" onClick={() => handleSort("id")}>
                                                <div className="flex justify-center items-center gap-1">
                                                    ID {sortIcon("id")}
                                                </div>
                                            </th>

                                            <th className="px-4 py-3 border border-indigo-300 cursor-pointer" onClick={() => handleSort("truckNo")}>
                                                <div className="flex justify-center items-center gap-1">
                                                    Truck No {sortIcon("truckNo")}
                                                </div>
                                            </th>

                                            <th className="px-4 py-3 border border-indigo-300 cursor-pointer" onClick={() => handleSort("entryDate")}>
                                                <div className="flex justify-center items-center gap-1">
                                                    Entry Date & Time {sortIcon("entryDate")}
                                                </div>
                                            </th>

                                            <th className="px-4 py-3 border border-indigo-300 cursor-pointer" onClick={() => handleSort("routeNo")}>
                                                <div className="flex justify-center items-center gap-1">
                                                    Route No {sortIcon("routeNo")}
                                                </div>
                                            </th>

                                            <th className="px-4 py-3 border border-indigo-300 cursor-pointer" onClick={() => handleSort("societyId")}>
                                                <div className="flex justify-center items-center gap-1">
                                                    Society ID {sortIcon("societyId")}
                                                </div>
                                            </th>

                                            <th className="px-4 py-3 border border-indigo-300 cursor-pointer" onClick={() => handleSort("compartments")}>
                                                <div className="flex justify-center items-center gap-1">
                                                    Compartments {sortIcon("compartments")}
                                                </div>
                                            </th>

                                            <th className="px-4 py-3 border border-indigo-300 cursor-pointer" onClick={() => handleSort("uniqueId")}>
                                                <div className="flex justify-center items-center gap-1">
                                                    Unique ID {sortIcon("uniqueId")}
                                                </div>
                                            </th>

                                            <th className="px-4 py-3 border border-indigo-300">Print QR</th>
                                            <th className="px-4 py-3 border border-indigo-300" hidden={(authUser?.roleId == 2) ? true : false}>Delete</th>
                                        </tr>
                                    </thead>

                                    <tbody className="text-gray-800 text-sm">
                                        {paginatedData.length > 0 ? (
                                            paginatedData.map((row) => (
                                                <tr key={row.id} className="hover:bg-indigo-50 transition border border-indigo-300">

                                                    <td className="px-4 py-3 border border-indigo-300">{row.id}</td>
                                                    <td className="px-4 py-3 border border-indigo-300">{row.truckNo}</td>

                                                    <td className="px-4 py-3 border border-indigo-300">
                                                        <div className="flex flex-col">
                                                            <span>{moment(row.entryDate).format("DD/MM/YYYY")}</span>
                                                            <span>{row.entryTime}</span>
                                                        </div>
                                                    </td>

                                                    <td className="px-4 py-3 border border-indigo-300">{row.routeNo}</td>
                                                    <td className="px-4 py-3 border border-indigo-300">{row.societyId}</td>
                                                    <td className="px-4 py-3 border border-indigo-300">{row.compartments}</td>
                                                    <td className="px-4 py-3 border border-indigo-300">{row.uniqueId}</td>

                                                    <td className="px-4 py-3 border border-indigo-300">
                                                        <div className="flex justify-center">
                                                            <FaPrint
                                                                size={18}
                                                                className="text-green-600 cursor-pointer hover:text-green-800"
                                                                onClick={() => handlePrint(row.id)}
                                                            />

                                                        </div>
                                                    </td>

                                                    <td className="px-4 py-3 border border-indigo-300" hidden={(authUser?.roleId == 2) ? true : false}>
                                                        <div className="flex justify-center">
                                                            <FaTrash
                                                                size={18}
                                                                className="text-red-600 cursor-pointer hover:text-red-800"
                                                                onClick={() => {
                                                                    setSelectedId(row.id);
                                                                    setDeleteModalOpen(true);
                                                                }}
                                                            />
                                                        </div>
                                                    </td>

                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10" className="text-center py-4 text-gray-500">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* PAGINATION BAR */}
                            <div className="flex justify-end items-center gap-5 mt-4 text-sm text-gray-700">

                                {/* Rows Per Page */}
                                <div className="flex items-center gap-2">
                                    <span>Rows Per Page:</span>

                                    <select
                                        value={rowsPerPage}
                                        onChange={handleRowsChange}
                                        className="border px-2 py-1 rounded-md text-sm shadow-sm"
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>

                                {/* X–Y of Z */}
                                <div>
                                    {startIndex + 1}-{endIndex} of {totalRows}
                                </div>

                                {/* Prev */}
                                <button
                                    className={`px-2 py-1 text-sm rounded-md font-semibold ${currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "text-indigo-600 hover:bg-indigo-100"
                                        }`}
                                    disabled={currentPage === 1}
                                    onClick={handlePrev}
                                >
                                    &lt;
                                </button>

                                {/* Current Page */}
                                <span className="w-6 h-6 flex items-center justify-center text-white bg-indigo-600 
                    rounded-full text-sm font-semibold shadow">
                                    {currentPage}
                                </span>

                                {/* Next */}
                                <button
                                    className={`px-2 py-1 text-sm rounded-md font-semibold ${currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : "text-indigo-600 hover:bg-indigo-100"
                                        }`}
                                    disabled={currentPage === totalPages}
                                    onClick={handleNext}
                                >
                                    &gt;
                                </button>
                            </div>
                        </div>

                        {/* DELETE MODAL */}
                        <DeleteConfirmModal
                            isOpen={deleteModalOpen}
                            toggle={toggleDeleteModal}
                            onConfirm={handleDeleteConfirmed}
                            itemName="Gate Entry"
                        />


                        {printModalOpen && (
                            <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 animate-fadeIn">

                                {/* Modal Box */}
                                <div className="bg-white w-80 sm:w-96 p-6 rounded-xl shadow-2xl transform animate-scaleIn">

                                    {/* Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full shadow">
                                            <FaPrint size={20} />
                                        </div>
                                        <h2 className="text-lg font-semibold text-indigo-700">
                                            Print Copies
                                        </h2>
                                    </div>

                                    {/* Description */}
                                    <p className="text-gray-600 text-sm mb-3">
                                        Please enter the number of copies you want to print.
                                    </p>

                                    {/* Input Field */}
                                    <input
                                        type="number"
                                        min="1"
                                        value={copies}
                                        onChange={(e) => setCopies(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                                        placeholder="Enter number of copies"
                                    />

                                    {/* Buttons */}
                                    <div className="flex justify-end gap-3 mt-6">

                                        {/* Confirm Button */}
                                        <button
                                            className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition font-medium shadow-md"
                                            onClick={confirmPrint}
                                        >
                                            Print
                                        </button>

                                        {/* Cancel Button */}
                                        <button
                                            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition font-medium shadow-md"
                                            onClick={() => setPrintModalOpen(false)}
                                        >
                                            Cancel
                                        </button>

                                    </div>

                                </div>
                            </div>
                        )}


                    </>
            }
        </>
    );
};

export default TankerEntryTable;
